import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@material-ui/core';
import { useParams, Redirect } from 'react-router-dom';
import Helmet from 'react-helmet';

import useStyles from './styles';

import ActionsCourse from '../../../store/ducks/course';
import SectionCard from '../../../components/SectionCard';

import TopBar from '../TopBar';
import SideMenu from '../SideMenu';
import Error from './Error';
import Loading from './Loading';
import Locked from './Locked';

const Certificate = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { course: courseId } = useParams();
  const {
    list: coursesObj,
    error: { list: listCoursesError },
    current,
  } = useSelector(state => state.course);
  const [courseInfos, setCourseInfos] = useState({});
  const [showSideMenu, setShowSideMenu] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const certificateHtml = React.useRef(null);

  const handleToggleSideMenu = useCallback((isVisible = null) => {
    setShowSideMenu(oldShowSideMenu =>
      typeof isVisible === 'boolean' ? isVisible : !oldShowSideMenu,
    );
  }, []);

  // Set active course and lesson on component mount
  useEffect(() => {
    const currentCourse = current?.course;
    const currentLesson = current?.lesson;
    if (currentCourse !== courseId || currentLesson) {
      dispatch(ActionsCourse.setActiveCourse(courseId, null));
      window.dispatchEvent(
        new CustomEvent('lessonChange', {
          detail: {
            courseId,
            lessonId: null,
            certificate: true,
          },
        }),
      );
    }
  }, [courseId, current, dispatch]);

  // Remove active course and lesson on component unmount
  useEffect(
    () => () => {
      dispatch(ActionsCourse.setActiveCourse(null));
    },
    [dispatch],
  );

  // Get/Set course infos
  useEffect(() => {
    if (
      (!(courseId in coursesObj) && !listCoursesError) ||
      !coursesObj[courseId].courseDetailed
    ) {
      dispatch(ActionsCourse.requestCourse(courseId));
    }
    if (courseId in coursesObj) {
      setCourseInfos(coursesObj[courseId]);
    }
  }, [courseId, coursesObj, listCoursesError, dispatch]);

  // Get course certificate html
  useEffect(() => {
    const certificate = courseInfos?.certificate;

    if (certificate) {
      setLoading(false);
    }

    if (certificate?.html === null && !certificate.error && !loadingHtml) {
      dispatch(ActionsCourse.requestCertificateHtml(courseId));
      setLoadingHtml(true);
    }

    if (certificate?.html !== null || !!certificate.error) {
      setLoadingHtml(false);
    }
  }, [courseId, courseInfos, dispatch, loadingHtml]);

  // eslint-disable-next-line no-control-regex
  const createMarkup = () => ({ __html: courseInfos.certificate.html });

  const certificateStatus = useMemo(() => {
    const certificate = courseInfos?.certificate;
    if (loadingHtml || loading) {
      return 'LOADING';
    }
    if (certificate?.locked) {
      return 'LOCKED';
    }
    if (certificate?.error) {
      return 'ERROR_HTML';
    }
    return 'READY';
  }, [courseInfos, loadingHtml, loading]);

  return (
    <Box className={classes.container}>
      <Helmet>
        <title>Certificado do curso</title>
      </Helmet>
      <Box className={`content ${!showSideMenu && 'without-sidemenu'}`}>
        <TopBar
          handleToggleSideMenu={handleToggleSideMenu}
          certificate
          certificateHtml={certificateHtml}
        />
        <SideMenu
          showSideMenu={showSideMenu}
          handleToggleSideMenu={handleToggleSideMenu}
          certificateActive
        />
        {courseInfos.courseDetailed && (
          <>
            <Box className="content-wrapper">
              <Box className="content-scrollable flexible">
                {courseInfos.certificate ? (
                  <SectionCard className="container-section">
                    {certificateStatus === 'ERROR_HTML' && <Error />}
                    {certificateStatus === 'LOADING' && <Loading />}
                    {certificateStatus === 'LOCKED' && <Locked />}
                    {certificateStatus === 'READY' && (
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={createMarkup()}
                        ref={certificateHtml}
                      />
                    )}
                  </SectionCard>
                ) : (
                  <Redirect to="/not-found" />
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Certificate;
